import { useState } from 'react';
import axios from 'axios';
import {
    Container,
    TextField,
    Button,
    Typography,
    Snackbar,
    Alert,
    Box,
    Divider,
    CircularProgress,
} from '@mui/material';
import Footer from '../components/Footer';
import { useThemeContext } from '../context/ThemeContext';
import { motion } from 'framer-motion';

export default function Contact() {
    const { theme } = useThemeContext();
    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const validateEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const submitForm = async () => {
        if (!form.name || !form.email || !form.message) {
            setErrorMessage('Please fill all fields');
            setError(true);
            return;
        }

        if (!validateEmail(form.email)) {
            setErrorMessage('Please enter a valid email address');
            setError(true);
            return;
        }

        try {
            setLoading(true);
            await axios.post('https://hotaps-backend.onrender.com/api/contact', form);
            // await axios.post('http://localhost:5000/api/contact', form);
            setOpen(true);
            setForm({ name: '', email: '', message: '' });
        } catch (err) {
            setErrorMessage('Failed to send message. Please try again later.');
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{
                flex: 1,
                backgroundColor: theme.bodyBackground,
                py: 10
            }}>
                <Container maxWidth="md" sx={{ mt: 10, mb: 6 }}>
                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                color: theme.bodyText,
                                mb: 2,
                            }}
                        >
                            Get in Touch
                        </Typography>
                        <Typography variant="h6" sx={{ color: theme.bodyText }}>
                            Have an idea or a project in mind? Let’s bring it to life.
                            Fill out the form below and our team will get back to you within 24 hours.
                        </Typography>
                    </Box>

                    {/* Contact Form with animation */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        <Box
                            sx={{
                                backgroundColor: theme.bodyBackground,
                                p: 4,
                                borderRadius: 3,
                            }}
                        >
                            <Typography variant="h5" sx={{ color: theme.bodyText, mb: 2 }}>
                                Mention your query and details here
                            </Typography>

                            <TextField
                                label="Name"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{
                                    style: { color: theme.bodyText }
                                }}
                                InputProps={{
                                    style: {
                                        backgroundColor: theme.bodyBackground,
                                        color: theme.bodyText,
                                    }
                                }}
                            />
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                InputLabelProps={{
                                    style: { color: theme.bodyText }
                                }}
                                InputProps={{
                                    style: {
                                        backgroundColor: theme.bodyBackground,
                                        color: theme.bodyText,
                                    }
                                }}
                            />
                            <TextField
                                label="Message"
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                multiline
                                rows={4}
                                InputLabelProps={{
                                    style: { color: theme.bodyText }
                                }}
                                InputProps={{
                                    style: {
                                        backgroundColor: '#101',
                                        color: theme.bodyText,
                                    }
                                }}
                            />

                            <Box mt={3} textAlign="right">
                                <Button
                                    variant="contained"
                                    onClick={submitForm}
                                    disabled={loading}
                                    startIcon={loading && <CircularProgress size={20} color="inherit" />}
                                >
                                    {loading ? 'Sending...' : 'Send Message'}
                                </Button>
                            </Box>
                        </Box>
                    </motion.div>

                    {/* Snackbar Feedback */}
                    <Snackbar
                        open={open}
                        autoHideDuration={3000}
                        onClose={() => setOpen(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            severity="success"
                            variant="filled"
                            sx={{
                                width: '100%',
                                backgroundColor: '#4caf50',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '1rem',
                                textAlign: 'center',
                                boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
                            }}
                        >
                            🎉 Message sent successfully!
                        </Alert>
                    </Snackbar>

                    <Snackbar
                        open={error}
                        autoHideDuration={5000}
                        onClose={() => setError(false)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            severity="error"
                            variant="filled"
                            sx={{
                                width: '100%',
                                backgroundColor: '#d32f2f',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textAlign: 'center',
                                boxShadow: '0 4px 15px rgba(211, 47, 47, 0.4)',
                            }}
                        >
                            ❌ Unable to send your message. Check your details or call us directly.
                            <br />
                        </Alert>
                    </Snackbar>


                    {/* Email fallback section */}
                    <Divider sx={{ my: 6 }} />
                    <Box textAlign="center">
                        <Typography variant="subtitle1" color={theme.bodyText}>
                            Want to directly reach out to our team for quick enquiry? Reach us at:
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                mt: 1,
                                fontWeight: 600,
                                color: theme.bodyText,
                                letterSpacing: 0.5,
                            }}
                        >
                            +91 9211845544
                        </Typography>
                    </Box>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
}
